import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import GameImage from '../components/GameImage';

const GamePage = ({ gameData }) => {
  const { categoryName, gameId } = useParams();
  const [game, setGame] = useState(null);
  const [relatedGames, setRelatedGames] = useState([]);
  
  useEffect(() => {
    if (gameData && gameData[categoryName]) {
      // Find the current game
      const currentGame = gameData[categoryName].find(g => g.id === gameId);
      setGame(currentGame);
      
      // Get related games from the same category
      if (currentGame) {
        // Get more related games (12 instead of 6)
        const related = gameData[categoryName]
          .filter(g => g.id !== gameId)
          .slice(0, 24); // Increased for more suggestions
        setRelatedGames(related);
      }
    }
    
    // Scroll to the top of the page when game changes with smooth behavior
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [gameData, categoryName, gameId]);

  if (!game) {
    // If the game is still loading, show a loading image
    return (
      <GamePageContainer>
        <LoadingImageWrapper>
          <LoadingImage src="/gameImages/lazy.png" alt="Loading..." />
        </LoadingImageWrapper>
      </GamePageContainer>
    );
  }

  return (
    <GamePageContainer>
      <GameHeader>
        <GameTitle>{game.name}</GameTitle>
        <BreadcrumbNav>
          <BreadcrumbLink to="/">Home</BreadcrumbLink> &gt; 
          <BreadcrumbLink to={`/category/${categoryName}`}> {categoryName} </BreadcrumbLink> &gt; 
          <span> {game.name}</span>
        </BreadcrumbNav>
      </GameHeader>
      
      {/* Game info and image side-by-side layout */}
      <GameImageAndInfoWrapper>
        {/* Game image on left side */}
        <GameImageWrapper>
          <GameImage 
            src={game.thumb300x300}
            fallbackSrc={game.thumb512x384}
            alt={game.name}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </GameImageWrapper>
        
        {/* Game info on right side */}
        <GameInfoWrapper>
          <GameDescription>
            <SectionTitle>About {game.name}</SectionTitle>
            <DescriptionText>{game.desc}</DescriptionText>
          </GameDescription>
          
          <GameMetadata>
            <SectionTitle>Game Details</SectionTitle>
            <MetadataItem>
              <MetadataLabel>Size:</MetadataLabel>
              <MetadataValue>{game.size}</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>Developer:</MetadataLabel>
              <MetadataValue>{game.developer || 'Unknown'}</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>Version:</MetadataLabel>
              <MetadataValue>{game.current_version || 'N/A'}</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>Updated:</MetadataLabel>
              <MetadataValue>{game.update || 'N/A'}</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>Rating:</MetadataLabel>
              <MetadataValue>{game.ratings} / 5.0</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>Category:</MetadataLabel>
              <MetadataValue>{game.tags}</MetadataValue>
            </MetadataItem>
          </GameMetadata>
        </GameInfoWrapper>
      </GameImageAndInfoWrapper>
      
      {/* Quick Game navigation - Left/Right suggestions */}
      <SideGameSuggestionsWrapper>
        {relatedGames.length > 0 && (
          <SideSuggestion side="left">
            <Link to={`/game/${categoryName}/${relatedGames[0].id}`}>
              <GameImage 
                src={relatedGames[0].thumb300x300} 
                fallbackSrc={relatedGames[0].thumb512x384} 
                alt={relatedGames[0].name} 
                style={{ width: '100%', height: '120px', objectFit: 'cover', display: 'block' }} 
              />
              <SideSuggestionOverlay>
                <SideSuggestionText>
                  <SideSuggestionArrow>←</SideSuggestionArrow>
                  {relatedGames[0].name}
                </SideSuggestionText>
              </SideSuggestionOverlay>
            </Link>
          </SideSuggestion>
        )}
        
        {relatedGames.length > 1 && (
          <SideSuggestion side="right">
            <Link to={`/game/${categoryName}/${relatedGames[1].id}`}>
              <GameImage 
                src={relatedGames[1].thumb300x300} 
                fallbackSrc={relatedGames[1].thumb512x384} 
                alt={relatedGames[1].name} 
                style={{ width: '100%', height: '120px', objectFit: 'cover', display: 'block' }} 
              />
              <SideSuggestionOverlay>
                <SideSuggestionText>
                  {relatedGames[1].name}
                  <SideSuggestionArrow>→</SideSuggestionArrow>
                </SideSuggestionText>
              </SideSuggestionOverlay>
            </Link>
          </SideSuggestion>
        )}
      </SideGameSuggestionsWrapper>

      {/* Related games section in multiple rows */}
      <RelatedGamesSection>
        <SectionTitle>You might also like</SectionTitle>
        
        {/* First row of games */}
        <RelatedGameGrid>
          {relatedGames.slice(2, 8).map(relatedGame => (
            <RelatedGameCard key={relatedGame.id}>
              <Link to={`/game/${categoryName}/${relatedGame.id}`}>
                <GameImage 
                  src={relatedGame.thumb300x300}
                  fallbackSrc={relatedGame.thumb512x384}
                  alt={relatedGame.name} 
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <RelatedGameName>{relatedGame.name}</RelatedGameName>
              </Link>
            </RelatedGameCard>
          ))}
        </RelatedGameGrid>
        
        {/* Second row of games */}
        <RelatedGameGrid style={{ marginTop: '20px' }}>
          {relatedGames.slice(8, 14).map(relatedGame => (
            <RelatedGameCard key={relatedGame.id}>
              <Link to={`/game/${categoryName}/${relatedGame.id}`}>
                <GameImage 
                  src={relatedGame.thumb300x300}
                  fallbackSrc={relatedGame.thumb512x384}
                  alt={relatedGame.name} 
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <RelatedGameName>{relatedGame.name}</RelatedGameName>
              </Link>
            </RelatedGameCard>
          ))}
        </RelatedGameGrid>
        
        {/* Third row of games */}
        <RelatedGameGrid style={{ marginTop: '20px' }}>
          {relatedGames.slice(14, 20).map(relatedGame => (
            <RelatedGameCard key={`extra-${relatedGame.id}`}>
              <Link to={`/game/${categoryName}/${relatedGame.id}`}>
                <GameImage 
                  src={relatedGame.thumb300x300}
                  fallbackSrc={relatedGame.thumb512x384}
                  alt={relatedGame.name} 
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <RelatedGameName>{relatedGame.name}</RelatedGameName>
              </Link>
            </RelatedGameCard>
          ))}
        </RelatedGameGrid>
        
        {/* Fourth row of games */}
        <RelatedGameGrid style={{ marginTop: '20px' }}>
          {relatedGames.slice(20, 24).map(relatedGame => (
            <RelatedGameCard key={`more-${relatedGame.id}`}>
              <Link to={`/game/${categoryName}/${relatedGame.id}`}>
                <GameImage 
                  src={relatedGame.thumb300x300}
                  fallbackSrc={relatedGame.thumb512x384}
                  alt={relatedGame.name} 
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <RelatedGameName>{relatedGame.name}</RelatedGameName>
              </Link>
            </RelatedGameCard>
          ))}
        </RelatedGameGrid>
      </RelatedGamesSection>
    </GamePageContainer>
  );
};

// Styling components
const GamePageContainer = styled.div`
  padding: 20px 0;
`;

const GameHeader = styled.header`
  margin-bottom: 20px;
`;

const GameTitle = styled.h1`
  margin: 0;
  font-size: 32px;
  margin-bottom: 15px;
`;

const BreadcrumbNav = styled.nav`
  margin-bottom: 20px;
`;

const BreadcrumbLink = styled(Link)`
  color: #333;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

// Side-by-side layout components
const GameImageAndInfoWrapper = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const GameImageWrapper = styled.div`
  flex: 1;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GameInfoWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LoadingImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const LoadingImage = styled.img`
  max-width: 100px;
`;

const GameDescription = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const DescriptionText = styled.div`
  line-height: 1.6;
  margin-top: 10px;
`;

const GameMetadata = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const MetadataItem = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const MetadataLabel = styled.span`
  font-weight: bold;
  margin-right: 10px;
  width: 100px;
`;

const MetadataValue = styled.span`
  color: #555;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
`;

const SideGameSuggestionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 30px 0;
`;

const SideSuggestion = styled.div`
  width: 49%;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  a {
    text-decoration: none;
    color: white;
  }
`;

const SideSuggestionOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px 15px;
`;

const SideSuggestionText = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
`;

const SideSuggestionArrow = styled.span`
  font-size: 20px;
  margin: 0 10px;
`;

const RelatedGamesSection = styled.section`
  margin: 40px 0;
`;

const RelatedGameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 20px;
  
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const RelatedGameCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  a {
    text-decoration: none;
    color: inherit;
  }
`;

const RelatedGameName = styled.div`
  padding: 10px;
  font-size: 14px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default GamePage;
